import { Injectable } from '@angular/core';

/**
 * Encryption Service for securing sensitive data in localStorage
 * 
 * Uses AES-256-GCM encryption with PBKDF2 key derivation
 * Provides transparent encryption/decryption for localStorage operations
 */
@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // 96 bits for GCM
  private readonly SALT_LENGTH = 16;
  private readonly TAG_LENGTH = 16;
  private readonly ITERATIONS = 100000;

  // Keys that should be encrypted when stored
  private readonly SENSITIVE_KEYS = [
    'token',
    'refresh_token',
    'user',
    'permissions',
    'role',
    'school',
    'campuses',
    'preferences',
    'fcm_token'
  ];

  constructor() {}

  /**
   * Generates a random string for use as encryption password
   */
  private generateRandomPassword(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Gets or creates the encryption key from sessionStorage
   * Using sessionStorage ensures the key is cleared when browser session ends
   */
  private getEncryptionPassword(): string {
    const stored = sessionStorage.getItem('_ek');
    if (stored) {
      return stored;
    }
    
    const newPassword = this.generateRandomPassword();
    sessionStorage.setItem('_ek', newPassword);
    return newPassword;
  }

  /**
   * Derives a crypto key from password using PBKDF2
   */
  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations: this.ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypts data using AES-GCM
   */
  private async encryptData(data: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);

      // Generate random salt and IV
      const salt = crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      // Derive encryption key
      const password = this.getEncryptionPassword();
      const key = await this.deriveKey(password, salt);

      // Encrypt the data
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
          tagLength: this.TAG_LENGTH * 8
        },
        key,
        dataBuffer
      );

      // Combine salt + iv + encrypted data
      const combined = new Uint8Array(
        this.SALT_LENGTH + this.IV_LENGTH + encryptedBuffer.byteLength
      );
      combined.set(salt, 0);
      combined.set(iv, this.SALT_LENGTH);
      combined.set(new Uint8Array(encryptedBuffer), this.SALT_LENGTH + this.IV_LENGTH);

      // Convert to base64 for storage
      return btoa(String.fromCodePoint(...combined));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts data using AES-GCM
   */
  private async decryptData(encryptedData: string): Promise<string> {
    try {
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(c => c.codePointAt(0) || 0)
      );

      // Extract salt, IV, and encrypted data
      const salt = combined.slice(0, this.SALT_LENGTH);
      const iv = combined.slice(this.SALT_LENGTH, this.SALT_LENGTH + this.IV_LENGTH);
      const encrypted = combined.slice(this.SALT_LENGTH + this.IV_LENGTH);

      // Derive decryption key
      const password = this.getEncryptionPassword();
      const key = await this.deriveKey(password, salt);

      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
          tagLength: this.TAG_LENGTH * 8
        },
        key,
        encrypted
      );

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Checks if a key should be encrypted
   */
  private shouldEncrypt(key: string): boolean {
    return this.SENSITIVE_KEYS.includes(key);
  }

  /**
   * Securely stores data in localStorage with encryption for sensitive keys
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      if (this.shouldEncrypt(key)) {
        const encryptedValue = await this.encryptData(value);
        localStorage.setItem(`_enc_${key}`, encryptedValue);
        // Remove any unencrypted version
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`Failed to securely store ${key}:`, error);
      // Fallback to unencrypted storage for non-critical data
      if (this.shouldEncrypt(key)) {
        throw error;
      } else {
        localStorage.setItem(key, value);
      }
    }
  }

  /**
   * Retrieves and decrypts data from localStorage
   */
  async getItem(key: string): Promise<string | null> {
    try {
      if (this.shouldEncrypt(key)) {
        // Try encrypted version first
        const encryptedValue = localStorage.getItem(`_enc_${key}`);
        if (encryptedValue) {
          return await this.decryptData(encryptedValue);
        }
        
        // Fallback to unencrypted version (for migration)
        const unencryptedValue = localStorage.getItem(key);
        if (unencryptedValue) {
          // Migrate to encrypted storage
          await this.setItem(key, unencryptedValue);
          localStorage.removeItem(key);
          return unencryptedValue;
        }
        
        return null;
      } else {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.error(`Failed to retrieve ${key}:`, error);
      // Fallback to unencrypted version if decryption fails
      return localStorage.getItem(key);
    }
  }

  /**
   * Removes an item from localStorage (both encrypted and unencrypted versions)
   */
  removeItem(key: string): void {
    if (this.shouldEncrypt(key)) {
      localStorage.removeItem(`_enc_${key}`);
    }
    localStorage.removeItem(key);
  }

  /**
   * Clears all sensitive data from localStorage
   */
  clearSensitiveData(): void {
    for (const key of this.SENSITIVE_KEYS) {
      this.removeItem(key);
    }
    // Clear the encryption key
    sessionStorage.removeItem('_ek');
  }

  /**
   * Migrates existing unencrypted data to encrypted storage
   */
  async migrateExistingData(): Promise<void> {
    const migrations: Promise<void>[] = [];
    
    for (const key of this.SENSITIVE_KEYS) {
      const value = localStorage.getItem(key);
      if (value && !localStorage.getItem(`_enc_${key}`)) {
        migrations.push(this.setItem(key, value));
      }
    }
    
    await Promise.all(migrations);
    console.log('Data migration to encrypted storage completed');
  }

  /**
   * Checks if the encryption service is available
   */
  isAvailable(): boolean {
    return typeof crypto !== 'undefined' && 
           crypto.subtle !== undefined && 
           crypto.getRandomValues !== undefined;
  }
}